/**
 * Platform SaaS staff roles (Structura ops) — NOT tenant Equipo RBAC.
 *
 * sm_users.role text values:
 * - Tenant: owner | staff
 * - Platform: super_admin | support | viewer
 */
import type { SmRole } from "@/domain/sm-models";
import type { CapabilityLevel } from "@structura/core/operational";

export const PLATFORM_STAFF_ROLES = ["super_admin", "support", "viewer"] as const;
export type PlatformStaffRole = (typeof PLATFORM_STAFF_ROLES)[number];

export type PlatformCapability =
  | "admin_read"
  | "admin_write"
  | "users_manage"
  | "impersonate"
  | "tickets_write"
  | "platform_staff_manage";

export const PLATFORM_CAPABILITY_LABELS: Record<PlatformCapability, string> = {
  admin_read: "Ver el panel de administración",
  admin_write: "Editar datos en el panel de administración",
  users_manage: "Gestionar usuarios de tenants",
  impersonate: "Impersonar cuentas para soporte",
  tickets_write: "Responder tickets de soporte",
  platform_staff_manage: "Gestionar staff de plataforma (crear/editar/eliminar)",
};

/**
 * SCRUM-477/478/479: mismo concepto de CapabilityLevel (none/read/write/admin)
 * que ya usa el modelo de permisos operativos del tenant (SCRUM-149,
 * ver capability-levels.ts) — no un sistema de niveles nuevo, solo aplicado
 * al catálogo de capabilities de plataforma.
 */
export interface PlatformCapabilitySpec {
  capability: PlatformCapability;
  supportsSubLevels: boolean;
  booleanEquivalentLevel: Exclude<CapabilityLevel, "none">;
  readDescription?: string;
  writeDescription?: string;
  adminDescription?: string;
}

export const PLATFORM_CAPABILITY_SPECS: Record<PlatformCapability, PlatformCapabilitySpec> = {
  admin_read: {
    capability: "admin_read",
    supportsSubLevels: false,
    booleanEquivalentLevel: "read",
    readDescription: "Ver el panel de administración (tenants, billing, flags, staff)",
  },
  admin_write: {
    capability: "admin_write",
    supportsSubLevels: true,
    booleanEquivalentLevel: "write",
    readDescription: "Ver el panel de administración sin modificar nada",
    writeDescription: "Editar configuración de tenants, flags e integraciones",
    adminDescription: "Editar y forzar cambios sensibles (overrides de plan, billing manual)",
  },
  users_manage: {
    capability: "users_manage",
    supportsSubLevels: true,
    booleanEquivalentLevel: "write",
    readDescription: "Ver usuarios de tenants y su estado",
    writeDescription: "Editar, desactivar o restablecer usuarios de tenants",
    adminDescription: "Eliminar usuarios y reasignar dueños de tenant",
  },
  impersonate: {
    capability: "impersonate",
    supportsSubLevels: false,
    booleanEquivalentLevel: "write",
    writeDescription: "Impersonar cuentas de tenants para soporte (con razón registrada)",
  },
  tickets_write: {
    capability: "tickets_write",
    supportsSubLevels: false,
    booleanEquivalentLevel: "write",
    writeDescription: "Responder y cerrar tickets de soporte",
  },
  platform_staff_manage: {
    capability: "platform_staff_manage",
    supportsSubLevels: true,
    booleanEquivalentLevel: "admin",
    readDescription: "Ver el staff de plataforma y sus perfiles",
    writeDescription: "Invitar y editar staff de plataforma",
    adminDescription: "Crear, editar y eliminar staff y perfiles de plataforma",
  },
};

export const ALL_CAPS: PlatformCapability[] = [
  "admin_read",
  "admin_write",
  "users_manage",
  "impersonate",
  "tickets_write",
  "platform_staff_manage",
];

/** Capability matrix for platform staff profiles. */
export const PLATFORM_ROLE_CAPABILITIES: Record<PlatformStaffRole, readonly PlatformCapability[]> = {
  super_admin: ALL_CAPS,
  support: ["admin_read", "impersonate", "tickets_write"],
  viewer: ["admin_read"],
};

export const PLATFORM_ROLE_LABELS: Record<PlatformStaffRole, string> = {
  super_admin: "Admin (full)",
  support: "Soporte",
  viewer: "Solo lectura",
};

export const PLATFORM_ROLE_DESCRIPTIONS: Record<PlatformStaffRole, string> = {
  super_admin: "Acceso total: tenants, billing, flags, staff, impersonación y tickets.",
  support: "Lectura de ops, atención de tickets e impersonación de tenants (con razón).",
  viewer: "Solo lectura del panel /admin (GET). Sin mutaciones ni impersonación.",
};

export function isPlatformStaffRole(role: string | null | undefined): role is PlatformStaffRole {
  return role === "super_admin" || role === "support" || role === "viewer";
}

export function hasPlatformCapability(
  role: string | null | undefined,
  capability: PlatformCapability,
): boolean {
  if (!isPlatformStaffRole(role)) return false;
  return PLATFORM_ROLE_CAPABILITIES[role].includes(capability);
}

/**
 * SCRUM-477/478/479 — mismo algoritmo de precedencia que
 * `resolveCapabilityLevel` (capability-levels.ts, tenant), aplicado al
 * catálogo de capabilities de plataforma. Se duplica la función (no el
 * concepto) porque tenant y plataforma son dos dominios de autorización
 * deliberadamente separados desde siempre en este archivo — mezclarlos
 * detrás de una sola función genérica acoplaría dos sistemas de roles que
 * hoy evolucionan por separado a propósito.
 */
export function resolvePlatformCapabilityLevel(
  capability: PlatformCapability,
  opts: {
    templateHasCapability: boolean;
    templateLevel?: CapabilityLevel | null;
  },
): CapabilityLevel {
  const spec = PLATFORM_CAPABILITY_SPECS[capability];
  if (!opts.templateHasCapability) return "none";
  if (opts.templateLevel != null && opts.templateLevel !== "none") return opts.templateLevel;
  return spec.booleanEquivalentLevel;
}

/** Roles allowed when creating platform staff (tenant_id null). */
export function parsePlatformStaffRole(raw: unknown): PlatformStaffRole | null {
  if (raw === "super_admin" || raw === "support" || raw === "viewer") return raw;
  return null;
}

export function platformRoleBadgeClass(role: string): string {
  if (role === "super_admin") return "bg-violet-500/15 text-violet-600 border-violet-500/30";
  if (role === "support") return "bg-sky-500/15 text-sky-600 border-sky-500/30";
  if (role === "viewer") return "bg-muted text-muted-foreground border-border";
  return "";
}

/** Soft type guard for SmRole after adding support. */
export function assertSmRoleIncludesSupport(_role: SmRole): void {
  /* compile-time reminder — support is a first-class SmRole */
}
